import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import * as Icons from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Gauge, Activity, ShieldCheck, MonitorCog, ArrowRight, AlertCircle,
  Download, FileText, RefreshCw,
} from "lucide-react";
import { Button } from "./ui/button";

import { cn } from "../lib/utils";
import { fmtMDT } from "../lib/time";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const STATUS_COLORS = {
  operational: "text-emerald-600 dark:text-emerald-400",
  degraded: "text-amber-600 dark:text-amber-400",
  down: "text-rose-600 dark:text-rose-400",
};

function Sparkline({ data, color = "#49C22E", height = 36 }) {
  const pts = (data || []).map((v, i) => ({ i, v }));
  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <AreaChart data={pts} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <defs>
            <linearGradient id="ucSparkGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.35} />
              <stop offset="95%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill="url(#ucSparkGrad)" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, tone = "primary", testid }) {
  const toneMap = {
    ok: "text-emerald-500",
    warn: "text-amber-500",
    bad: "text-rose-500",
    primary: "text-primary",
  };
  return (
    <div className="rounded-xl border border-border bg-card p-3.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Icon className={`w-3.5 h-3.5 ${toneMap[tone] || "text-primary"}`} /> {label}
      </div>
      <div className="mt-1 text-[22px] font-semibold tracking-tight leading-none">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function downloadCsv(name, rows) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}

export default function UCVigilStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/uc-vigil/summary");
      setData(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const trendPoints = useMemo(() => {
    if (!data) return [];
    const days = 30;
    return Array.from({ length: days }, (_, i) => ({
      day: `D-${days - i}`,
      compliance: data.trends.compliance[i],
      focus: data.trends.focus[i],
      blocked: data.trends.blocked[i],
      endpoints: data.trends.endpoints_online[i],
    }));
  }, [data]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["app", "category", "status", "uptime_30d", "metric_label", "metric_value", "metric_trend", "last_incident"]];
    data.apps.forEach((a) => rows.push([a.name, a.category, a.status, a.uptime_30d, a.metric_label, a.metric_value, a.metric_trend, a.last_incident]));
    downloadCsv(`uc-vigil-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const exportPdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFontSize(18); doc.text("UC Vigil — Aggregate Report", 40, 50);
    doc.setFontSize(10); doc.setTextColor(120); doc.text(`Generated ${fmtMDT(data.generated_at)}`, 40, 68);
    doc.setTextColor(20);
    const k = data.kpis;
    const kpiLines = [
      ["Total apps", k.total_apps],
      ["Operational", k.operational],
      ["Degraded", k.degraded],
      ["Down", k.down],
      ["Avg uptime (30d)", `${k.avg_uptime_30d}%`],
      ["Controls passing", k.controls_passing],
      ["Focus score", k.focus_score],
      ["Blocked events (24h)", k.blocked_events_24h],
    ];
    autoTable(doc, { startY: 90, head: [["KPI", "Value"]], body: kpiLines, styles: { fontSize: 10 }, headStyles: { fillColor: [95, 142, 237] } });
    autoTable(doc, {
      head: [["App", "Category", "Status", "Uptime 30d", "Metric", "Last incident"]],
      body: data.apps.map((a) => [a.name, a.category, a.status, `${a.uptime_30d}%`, `${a.metric_label || ""}: ${a.metric_value || ""}`, fmtMDT(a.last_incident, { withTz: false })]),
      styles: { fontSize: 9 }, headStyles: { fillColor: [95, 142, 237] },
    });
    doc.save(`uc-vigil-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  if (loading || !data) {
    return (
      <section className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-muted/40 animate-pulse" />)}
        </div>
        <div className="h-56 rounded-xl bg-muted/30 animate-pulse" />
      </section>
    );
  }
  const k = data.kpis;

  return (
    <section className="space-y-4" data-testid="uc-vigil-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Gauge className="w-4 h-4 text-primary" />
          </div>
          <h3 className="text-[15px] font-semibold">UC Vigil</h3>
          <span className="text-[11.5px] text-muted-foreground">Aggregated compliance & health · 30 days</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 h-8" data-testid="uc-vigil-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} className="gap-1.5 h-8" data-testid="uc-vigil-csv-btn">
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
          <Button size="sm" onClick={exportPdf} className="gap-1.5 h-8" data-testid="uc-vigil-pdf-btn">
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <Kpi icon={Activity}     label="Apps"        value={k.total_apps}                       hint={`${k.operational} up · ${k.degraded} deg · ${k.down} down`} tone="primary" testid="ucv-kpi-apps" />
        <Kpi icon={MonitorCog}   label="Uptime 30d"  value={`${k.avg_uptime_30d}%`}                                                                              tone="ok"      testid="ucv-kpi-uptime" />
        <Kpi icon={ShieldCheck}  label="Controls"    value={k.controls_passing}                  hint="Compliance Vault"                                          tone="ok"      testid="ucv-kpi-controls" />
        <Kpi icon={AlertCircle}  label="Blocked 24h" value={k.blocked_events_24h}                 hint="ThreatLocker"                                              tone="warn"    testid="ucv-kpi-blocked" />
        <Kpi icon={Activity}     label="Endpoints"   value={data.trends.endpoints_online.at(-1)} hint="NinjaOne"                                                  tone="ok"      testid="ucv-kpi-endpoints" />
      </div>

      {/* Trend charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-card p-3.5" data-testid="ucv-chart-compliance">
          <div className="flex items-center justify-between">
            <h4 className="text-[13px] font-semibold">Compliance controls passing</h4>
            <span className="text-[11px] text-muted-foreground">30 days</span>
          </div>
          <div className="mt-1 h-40">
            <ResponsiveContainer>
              <LineChart data={trendPoints}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={5} />
                <YAxis domain={["dataMin - 2", "dataMax + 2"]} tick={{ fontSize: 10 }} width={30} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="compliance" stroke="#49C22E" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3.5" data-testid="ucv-chart-blocked">
          <div className="flex items-center justify-between">
            <h4 className="text-[13px] font-semibold">Blocked events (ThreatLocker)</h4>
            <span className="text-[11px] text-muted-foreground">30 days</span>
          </div>
          <div className="mt-1 h-40">
            <ResponsiveContainer>
              <AreaChart data={trendPoints}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={5} />
                <YAxis tick={{ fontSize: 10 }} width={30} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="blocked" stroke="hsl(0 72% 51%)" fill="hsl(0 72% 51% / 0.15)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Per-app health table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="ucv-apps-table">
        <div className="px-3.5 py-2.5 border-b border-border flex items-center justify-between">
          <h4 className="text-[13px] font-semibold">Per-app health</h4>
          <span className="text-[11px] text-muted-foreground">{data.apps.length} apps</span>
        </div>
        <div className="hidden md:grid grid-cols-12 gap-3 px-3.5 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
          <div className="col-span-3">App</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Uptime 30d</div>
          <div className="col-span-3">30-day sparkline</div>
        </div>
        <div className="divide-y divide-border">
          {data.apps.map((a) => {
            const Icon = Icons[a.icon] || Icons.LayoutGrid;
            return (
              <div key={a.id} className="grid grid-cols-12 gap-3 items-center px-3.5 py-2.5 hover:bg-muted/30">
                <div className="col-span-6 md:col-span-3 flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span className="font-semibold text-[12.5px] truncate">{a.name}</span>
                </div>
                <div className="hidden md:block col-span-2 text-[12px] text-muted-foreground capitalize">{String(a.category).replace("_", " ")}</div>
                <div className={cn("col-span-3 md:col-span-2 text-[12px] font-semibold capitalize", STATUS_COLORS[a.status])}>{a.status}</div>
                <div className="col-span-3 md:col-span-2 text-[12px] font-semibold">{a.uptime_30d}%</div>
                <div className="col-span-12 md:col-span-3"><Sparkline data={a.uptime_series} /></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
